import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { IDENTIFIER_FIELD_PROPS } from '@/components/shared/touchInput';
import { Popover, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
/**
 * Type-ahead single-select. A native-looking field that opens the full list on
 * focus, filters it as you type (case-insensitive substring on the label), and
 * is driven by mouse or keyboard (arrows, Enter, Escape). WAI-ARIA combobox:
 * the input keeps focus and points at the active option via
 * aria-activedescendant. The list is a non-modal Popover, so inside a modal
 * Dialog it layers above and picking an option never closes the Dialog.
 *
 * Not `<input list>` + `<datalist>`: it reports the label rather than an id,
 * cannot be styled to match the app's fields, and only shows the full list on
 * focus in some browsers.
 */
export function Combobox({ value, onChange, options, allOption, placeholder, noMatchText, disabled, id, 'aria-label': ariaLabel, 'data-testid': testId, className, wrapperClassName, }) {
    const { t } = useTranslation();
    const reactId = useId();
    const inputId = id ?? `${reactId}-input`;
    const listId = `${reactId}-list`;
    const [open, setOpen] = useState(false);
    // null = show the selected label; a string = the user is filtering.
    const [query, setQuery] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const anchorRef = useRef(null);
    const all = useMemo(() => (allOption ? [allOption, ...options] : [...options]), [allOption, options]);
    const selected = all.find((o) => o.value === value) ?? null;
    const filtered = useMemo(() => {
        const q = (query ?? '').trim().toLowerCase();
        return q ? all.filter((o) => o.label.toLowerCase().includes(q)) : all;
    }, [all, query]);
    const active = Math.min(activeIndex, filtered.length - 1);
    const openList = () => {
        if (disabled)
            return;
        setQuery(null);
        setActiveIndex(Math.max(0, all.findIndex((o) => o.value === value)));
        setOpen(true);
    };
    const closeList = () => {
        setOpen(false);
        setQuery(null);
    };
    const choose = (option) => {
        onChange(option.value);
        closeList();
    };
    const move = (next) => {
        const clamped = Math.max(0, Math.min(filtered.length - 1, next));
        setActiveIndex(clamped);
        document.getElementById(`${listId}-opt-${clamped}`)?.scrollIntoView?.({ block: 'nearest' });
    };
    const onKeyDown = (e) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!open)
                    openList();
                else
                    move(active + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!open)
                    openList();
                else
                    move(active - 1);
                break;
            case 'Enter':
                if (open) {
                    e.preventDefault();
                    if (filtered[active])
                        choose(filtered[active]);
                }
                break;
            case 'Escape':
                if (open) {
                    e.preventDefault();
                    e.stopPropagation();
                    closeList();
                }
                break;
            case 'Tab':
                closeList();
                break;
        }
    };
    return (_jsxs(Popover, { open: open && !disabled, onOpenChange: (next) => !next && closeList(), children: [_jsx(PopoverPrimitive.Anchor, { asChild: true, children: _jsxs("div", { ref: anchorRef, className: cn('relative w-full', wrapperClassName), children: [_jsx("input", { id: inputId, type: "text", role: "combobox", "aria-expanded": open, "aria-controls": open ? listId : undefined, "aria-activedescendant": open && filtered[active] ? `${listId}-opt-${active}` : undefined, "aria-autocomplete": "list", "aria-label": ariaLabel, "data-testid": testId, autoComplete: "off", ...IDENTIFIER_FIELD_PROPS, enterKeyHint: "done", disabled: disabled, placeholder: placeholder, value: query ?? selected?.label ?? '', onChange: (e) => {
                                setQuery(e.target.value);
                                setActiveIndex(0);
                                setOpen(true);
                            }, onFocus: (e) => {
                                openList();
                                e.currentTarget.select();
                            }, onClick: () => {
                                if (!open)
                                    openList();
                            }, onBlur: closeList, onKeyDown: onKeyDown, className: cn('border-input bg-background ring-offset-background placeholder:text-muted-foreground focus:ring-ring h-10 w-full rounded-md border py-2 pr-8 pl-3 text-sm text-ellipsis focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50', className) }), _jsx(ChevronDown, { "aria-hidden": "true", className: "pointer-events-none absolute top-1/2 right-2.5 h-4 w-4 -translate-y-1/2 opacity-50" })] }) }), _jsxs(PopoverContent, { align: "start", scrollable: false, 
                // Focus stays in the input; the list is driven by aria-activedescendant.
                onOpenAutoFocus: (e) => e.preventDefault(), onCloseAutoFocus: (e) => e.preventDefault(), 
                // A press on the field itself is not "outside": it would close and
                // immediately reopen the list.
                onInteractOutside: (e) => {
                    if (e.target instanceof Node && anchorRef.current?.contains(e.target))
                        e.preventDefault();
                }, 
                // Keep input focus when pressing the list or its scrollbar.
                onMouseDown: (e) => e.preventDefault(), 
                // A modal Dialog's scroll lock swallows wheel/touch moves outside it;
                // this list is portalled outside, so it must handle its own scrolling.
                onWheel: (e) => e.stopPropagation(), onTouchMove: (e) => e.stopPropagation(), className: "w-(--radix-popover-trigger-width) min-w-40 p-1", children: [_jsx("ul", { id: listId, role: "listbox", "aria-label": ariaLabel, className: cn('max-h-60 overflow-y-auto', filtered.length === 0 && 'hidden'), children: filtered.map((o, i) => (_jsxs("li", { id: `${listId}-opt-${i}`, role: "option", "aria-selected": o.value === value, onClick: () => choose(o), onMouseMove: () => i !== active && setActiveIndex(i), className: cn('flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm pointer-coarse:py-2.5', i === active && 'bg-accent text-accent-foreground'), children: [_jsx(Check, { "aria-hidden": "true", className: cn('h-3.5 w-3.5 shrink-0', o.value === value ? 'opacity-100' : 'opacity-0') }), _jsx("span", { className: "min-w-0 flex-1 truncate", children: o.label }), o.hint && _jsx("span", { className: "text-muted-foreground shrink-0 text-xs", children: o.hint })] }, o.value))) }), filtered.length === 0 && (_jsx("p", { role: "status", className: "text-muted-foreground px-2 py-3 text-center text-xs", children: noMatchText ?? t('combobox.noMatches') }))] })] }));
}
